import Fee from "_shared/fee-schedules/categories/fees/fee";
import { Change, diffLines } from "diff";
import { TextChangeJSON } from "work/proposals/redlining/__diff/_schema/text-change-schema";

import TextChange, { TextChangeCollection } from "work/proposals/redlining/__diff/text-change";

export interface FeeRedlineJSON {
  changes: TextChangeJSON[];
  _originalFee: FeeType;
  _revisedFee: FeeType;
  _currentFee: FeeType | undefined;
}
export type FeeType = Fee | null | false;
export default class FeeRedline extends TextChangeCollection {
  private readonly _originalEntry: FeeType;
  private readonly _revisedEntry: FeeType;
  public _currentEntry: FeeType | undefined;

  constructor(originalFee: FeeType, revisedFee: FeeType, textChanges?: TextChange[]) {
    super(textChanges || []);

    this._originalEntry = originalFee;
    this._revisedEntry = revisedFee;
    if (this._originalEntry === false && this._revisedEntry === false) {
      this._currentEntry = false;
    }
    if (this._originalEntry === null && this._revisedEntry === null) {
      this._currentEntry = null;
    }
    if (this._originalEntry && this._revisedEntry && this._originalEntry.isEqualTo(this._revisedEntry)) {
      this._currentEntry = revisedFee;
    }

    this.updateDiff();
  }

  public get isAdded(): boolean {
    if (this._currentEntry === undefined){
      return this._originalEntry === false && this._revisedEntry !== false;
    }
    if (this._currentEntry === false) return false;
    if (this._currentEntry) return this.revisedEntry === false;
    return false;
  }

  public get isRemoved(): boolean {
    if (this._currentEntry === undefined){
      return this._originalEntry !== false && this._revisedEntry === false;
    }
    return this._currentEntry === false;
  }

  public get isResolved(): boolean {
    return this._currentEntry !== undefined;
  }

  public get originalEntry(): FeeType {
    return this._originalEntry;
  }

  public get revisedEntry(): FeeType {
    return this._revisedEntry;
  }

  public get currentEntry(): FeeType | undefined {
    return this._currentEntry;
  }

  public get wasRedlined(): boolean {
    if (
      this._originalEntry &&
      this._revisedEntry &&
      this._originalEntry.isEqualTo(this._revisedEntry)
    )
      return false;
    return this._originalEntry !== this._revisedEntry;
  }

  public get wasModified(): boolean {
    if (this._revisedEntry && this._currentEntry) return !this._revisedEntry.isEqualTo(this._currentEntry);
    if (this.revisedEntry === null) return this.currentEntry !== null;
    if (this.revisedEntry === false) return this.currentEntry !== false;
    return false;
  }

  public get canBeUndone(): boolean {
    return (
      (this.wasRedlined && this.isResolved) ||
      this.wasModified
    );
  }

  public updateFee(newFee: Fee | null | undefined | false): FeeRedline {
    const newRedline = this.clone();
    newRedline._currentEntry = newFee;
    newRedline.updateDiff();
    return newRedline;
  }

  private updateDiff() {
    const newChanges: TextChange[] = [];
    let diffResult: Change[];
    let originalLine: string = "";
    let revisedLine: string = "";
    let currentLine: string = "";

    if (this._originalEntry === null){
      originalLine = "Deferred";
    } else if (this._originalEntry){
      originalLine = this._originalEntry.toString();
    }
    if (this._revisedEntry === null){
      revisedLine = "Deferred";
    } else if (this._revisedEntry){
      revisedLine = this._revisedEntry.toString();
    }
    if (this._currentEntry === null){
      currentLine = "Deferred";
    } else if (this._currentEntry){
      currentLine = this._currentEntry.toString();
    }


    if (this._currentEntry === undefined) {
      diffResult = diffLines(originalLine, revisedLine);
      for (const change of diffResult) {
        newChanges.push(new TextChange({ parent: this, diff: change }));
      }
    } else {
      diffResult = diffLines(revisedLine, currentLine);
      for (const change of diffResult) {
        newChanges.push(new TextChange({ parent: this, diff: change }));
      }
    }
    this._textChanges = newChanges;
  }

  public clone() {
    return FeeRedline.fromJSON(this.toFeeRedlineJSON());
  }

  public toFeeRedlineJSON(): FeeRedlineJSON {
    return {
      changes: this.textChanges.map((change: TextChange) => change.toJSON()),
      _originalFee: this._originalEntry,
      _revisedFee: this._revisedEntry,
      _currentFee: this._currentEntry,
    };
  }

  public static fromJSON(json: FeeRedlineJSON): FeeRedline {
    const originalFee = json._originalFee ? Fee.fromJSON(json._originalFee) : json._originalFee;
    const revisedFee = json._revisedFee ? Fee.fromJSON(json._revisedFee) : json._revisedFee;
    const redline = new FeeRedline(originalFee, revisedFee);

    let currentFee: FeeType | undefined = undefined;
    if (json._currentFee === null) currentFee = null;
    else if (json._currentFee === false) currentFee = false;
    else if (json._currentFee === undefined) currentFee = undefined;
    else if (json._currentFee) currentFee = Fee.fromJSON(json._currentFee);

    redline._currentEntry = currentFee;
    redline.updateDiff();
    return redline;
  }
}
